import { useEffect, useState } from "react";
import {
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  BookOpen,
  CircleDollarSign,
  Loader2,
  Plus,
  Trash2,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, today } from "@/lib/api";
import { downloadCsv } from "@/lib/exportCsv";
import { PageHeader, EmptyState, Money } from "@/components/common";
import { CASH_IN_CATEGORIES, CASH_OUT_CATEGORIES, OP_CATEGORIES, can } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

function SaldoChip({ label, value, accent }) {
  return (
    <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4" data-testid={`saldo-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">{label}</p>
      <Money value={value} className={`text-lg font-semibold ${accent}`} />
    </div>
  );
}

export default function CashBook() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opOpen, setOpOpen] = useState(false);
  const [opForm, setOpForm] = useState(null);
  const [opBusy, setOpBusy] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => {
      setBranches(r.data);
      if (!branch) {
        if (user?.role === "admin_cabang") setBranch(user.branch_id || "");
        else if (r.data.length) setBranch(r.data[0].id);
      }
    });
  }, [user]);

  const load = () => {
    if (!branch) return;
    const params = new URLSearchParams({ branch_id: branch });
    if (month) params.set("month", month);
    api.get(`/cash-entries?${params}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [branch, month]);

  useEffect(() => {
    if (open) setForm({ type: "keluar", category: "biaya_mobil", amount: "", date: today(), description: "" });
  }, [open]);

  useEffect(() => {
    if (transferOpen) {
      setTransferForm({
        from_branch_id: branch === "umum" ? "" : branch,
        to_branch_id: "",
        amount: "",
        date: today(),
        description: "",
      });
    }
  }, [transferOpen, branch]);

  useEffect(() => {
    if (opOpen) setOpForm({ category: OP_CATEGORIES[0], amount: "", date: today(), description: "" });
  }, [opOpen]);

  const submitOp = async (e) => {
    e.preventDefault();
    setOpBusy(true);
    try {
      await api.post("/op-costs", { ...opForm, branch_id: branch, cash_branch_id: branch, amount: parseFloat(opForm.amount) || 0 });
      toast.success("Biaya operasional dicatat & masuk ke buku kas");
      setOpOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setOpBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cash-entries", { ...form, branch_id: branch, amount: parseFloat(form.amount) || 0 });
      toast.success("Entri kas berhasil dicatat");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus entri kas ini?")) return;
    try {
      await api.delete(`/cash-entries/${id}`);
      toast.success("Entri kas dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markOperational = async (id) => {
    if (!window.confirm("Tandai entri ini sebagai biaya operasional?")) return;
    try {
      await api.post(`/cash-entries/${id}/mark-operational`);
      toast.success("Entri kini dihitung sebagai biaya operasional");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    setTransferBusy(true);
    try {
      await api.post("/cash-transfers", {
        ...transferForm,
        amount: parseFloat(transferForm.amount) || 0,
      });
      toast.success("Transfer antar cabang berhasil dicatat");
      setTransferOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTransferBusy(false);
    }
  };

  const categories = form?.type === "masuk" ? CASH_IN_CATEGORIES : CASH_OUT_CATEGORIES;
  const manualCategories = Object.entries(categories).filter(
    ([key]) => !key.startsWith("transfer_")
  );
  const transferSources = user?.role === "admin_cabang"
    ? branches.filter((item) => item.id === user.branch_id)
    : branches;
  const transferDestinations = branches.filter((item) => {
    if (item.id === transferForm?.from_branch_id) return false;
    return user?.role !== "admin_cabang" || item.is_pusat;
  });

  return (
    <div data-testid="cashbook-page">
      <PageHeader title="Buku Kas" subtitle="Arus kas per cabang — biaya mobil & operasional tercatat otomatis">
        {user?.role === "admin_cabang" ? (
          <span className="h-10 px-3 inline-flex items-center text-sm text-zinc-300 bg-[#0A0A0C] border border-[#1A1A22] rounded-md">
            {branches.find((b) => b.id === branch)?.name || "Cabang Anda"}
          </span>
        ) : (
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="cashbook-branch-select" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
              <SelectItem value="umum">Kas Umum (Tanpa Cabang)</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input data-testid="cashbook-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        <Button
          data-testid="export-cashbook-button"
          variant="outline"
          onClick={() => {
            downloadCsv(`buku-kas-${month || "semua"}.csv`,
              ["Tanggal", "Keterangan", "Kategori", "Masuk", "Keluar", "Dicatat Oleh"],
              (data?.entries || []).map((e) => [e.date, e.description, e.category, e.type === "masuk" ? e.amount : 0, e.type === "keluar" ? e.amount : 0, e.created_by_name || "-"]));
            toast.success("File Excel (CSV) buku kas diunduh");
          }}
          className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10"
        >
          Unduh Excel
        </Button>
        {can(user, "input_biaya_operasional") && (
          <Button
            data-testid="add-op-cost-button"
            variant="outline"
            onClick={() => setOpOpen(true)}
            disabled={!branch || branch === "umum"}
            className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10"
          >
            <Wrench className="w-4 h-4 mr-1" /> Biaya Operasional
          </Button>
        )}
        {can(user, "kas_manual") && (
          <>
            <Button
              data-testid="create-cash-transfer-button"
              variant="outline"
              onClick={() => setTransferOpen(true)}
              disabled={branches.length < 2}
              className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10"
            >
              <ArrowLeftRight className="w-4 h-4 mr-1" /> Transfer Kas
            </Button>
            <Button data-testid="add-cash-entry-button" onClick={() => setOpen(true)} disabled={!branch} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
              <Plus className="w-4 h-4 mr-1" /> Catat Kas
            </Button>
          </>
        )}
      </PageHeader>

      {!data ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 fade-up">
            <SaldoChip label="Saldo Awal" value={data.saldo_awal} accent="text-zinc-100" />
            <SaldoChip label="Kas Masuk" value={data.total_masuk} accent="text-emerald-500" />
            <SaldoChip label="Kas Keluar" value={data.total_keluar} accent="text-rose-500" />
            <SaldoChip label="Saldo Akhir" value={data.saldo_akhir} accent="text-blue-500" />
          </div>

          {data.entries.length === 0 ? (
            <EmptyState icon={BookOpen} title="Belum ada entri kas" desc="Catat kas masuk & keluar untuk cabang ini" />
          ) : (
            <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
              <table className="w-full text-sm min-w-[760px]" data-testid="cashbook-table">
                <thead>
                  <tr className="border-b border-[#1A1A22] text-left">
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Keterangan</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Kategori</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Dicatat Oleh</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Masuk</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Keluar</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Saldo</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                      <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-4 py-3 text-zinc-100">
                        {e.description || "-"}
                        {e.ref_kind && (
                          <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider border text-zinc-400 bg-zinc-500/10 border-zinc-600/30">Otomatis</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${e.type === "masuk" ? "text-emerald-500" : "text-rose-500"}`}>
                          {e.type === "masuk" ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
                          {(e.type === "masuk" ? CASH_IN_CATEGORIES : CASH_OUT_CATEGORIES)[e.category]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-400">{e.created_by_name || "-"}</td>
                      <td className="px-4 py-3 text-right">{e.type === "masuk" ? <Money value={e.amount} className="text-emerald-500" /> : <span className="text-zinc-700">-</span>}</td>
                      <td className="px-4 py-3 text-right">{e.type === "keluar" ? <Money value={e.amount} className="text-rose-500" /> : <span className="text-zinc-700">-</span>}</td>
                      <td className="px-4 py-3 text-right"><Money value={e.saldo} className="text-zinc-100" /></td>
                      <td className="px-4 py-3 text-right">
                        {user?.role === "admin" && !e.ref_kind && (
                          <div className="flex justify-end gap-2">
                            {e.type === "keluar" && e.category === "lain_lain" && (
                              <button
                                type="button"
                                title="Jadikan biaya operasional"
                                aria-label="Jadikan biaya operasional"
                                data-testid={`mark-cash-operational-${e.id}`}
                                onClick={() => markOperational(e.id)}
                                className="text-zinc-600 hover:text-amber-400 transition-colors duration-200"
                              >
                                <CircleDollarSign className="w-4 h-4 inline mr-1" />
                                Operasional
                              </button>
                            )}
                            <button data-testid={`delete-cash-entry-${e.id}`} onClick={() => remove(e.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Catat Kas</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4" data-testid="cash-entry-form">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="cash-type-masuk"
                  onClick={() => setForm((f) => ({ ...f, type: "masuk", category: "penjualan" }))}
                  className={`h-10 rounded-md text-sm font-semibold border transition-colors duration-200 ${form.type === "masuk" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" : "text-zinc-400 border-[#1A1A22] hover:bg-[#121218]"}`}
                >
                  Kas Masuk
                </button>
                <button
                  type="button"
                  data-testid="cash-type-keluar"
                  onClick={() => setForm((f) => ({ ...f, type: "keluar", category: "biaya_mobil" }))}
                  className={`h-10 rounded-md text-sm font-semibold border transition-colors duration-200 ${form.type === "keluar" ? "bg-rose-500/10 text-rose-500 border-rose-500/30" : "text-zinc-400 border-[#1A1A22] hover:bg-[#121218]"}`}
                >
                  Kas Keluar
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className={labelCls}>Kategori</Label>
                  <Select key={form.type} value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="cash-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                      {manualCategories.map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Tanggal</Label>
                  <Input data-testid="cash-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Jumlah (Rp)</Label>
                <Input data-testid="cash-amount-input" type="number" min="1" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="5000000" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Keterangan</Label>
                <Input data-testid="cash-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Contoh: bayar body repair Avanza" className={inputCls} />
              </div>
              <Button data-testid="cash-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Entri Kas"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={opOpen} onOpenChange={setOpOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Biaya Operasional</DialogTitle>
            <p className="text-xs text-zinc-500">Dicatat sebagai beban operasional & otomatis keluar dari kas {branches.find((b) => b.id === branch)?.name || ""}</p>
          </DialogHeader>
          {opForm && (
            <form onSubmit={submitOp} className="space-y-4" data-testid="op-cost-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={labelCls}>Kategori</Label>
                  <Select value={opForm.category} onValueChange={(v) => setOpForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="op-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                      {OP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Tanggal</Label>
                  <Input data-testid="op-date-input" type="date" required value={opForm.date} onChange={(e) => setOpForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Jumlah (Rp)</Label>
                <Input data-testid="op-amount-input" type="number" min="1" required value={opForm.amount} onChange={(e) => setOpForm((f) => ({ ...f, amount: e.target.value }))} placeholder="2500000" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Keterangan</Label>
                <Input data-testid="op-desc-input" value={opForm.description} onChange={(e) => setOpForm((f) => ({ ...f, description: e.target.value }))} placeholder="Contoh: listrik bulan Juni" className={inputCls} />
              </div>
              <Button data-testid="op-submit-button" type="submit" disabled={opBusy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
                {opBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Biaya Operasional"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Transfer Kas</DialogTitle>
          </DialogHeader>
          {transferForm && (
            <form
              onSubmit={submitTransfer}
              className="space-y-4"
              data-testid="cash-transfer-form"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className={labelCls}>Dari Cabang</Label>
                  <Select
                    value={transferForm.from_branch_id}
                    onValueChange={(v) => {
                      setTransferForm((f) => ({ ...f, from_branch_id: v }));
                    }}
                  >
                    <SelectTrigger data-testid="transfer-from-branch-select" className={inputCls}>
                      <SelectValue placeholder="Pilih asal" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                      {transferSources.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Ke Cabang</Label>
                  <Select
                    value={transferForm.to_branch_id}
                    onValueChange={(v) => {
                      setTransferForm((f) => ({ ...f, to_branch_id: v }));
                    }}
                  >
                    <SelectTrigger data-testid="transfer-to-branch-select" className={inputCls}>
                      <SelectValue placeholder="Pilih tujuan" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                      {transferDestinations.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className={labelCls}>Tanggal</Label>
                  <Input
                    data-testid="transfer-date-input"
                    type="date"
                    required
                    value={transferForm.date}
                    onChange={(e) => {
                      setTransferForm((f) => ({ ...f, date: e.target.value }));
                    }}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Jumlah (Rp)</Label>
                  <Input
                    data-testid="transfer-amount-input"
                    type="number"
                    min="1"
                    required
                    value={transferForm.amount}
                    onChange={(e) => {
                      setTransferForm((f) => ({ ...f, amount: e.target.value }));
                    }}
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Keterangan</Label>
                <Input
                  data-testid="transfer-description-input"
                  value={transferForm.description}
                  onChange={(e) => {
                    setTransferForm((f) => ({ ...f, description: e.target.value }));
                  }}
                  placeholder="Contoh: setoran kas harian"
                  className={inputCls}
                />
              </div>
              <Button
                data-testid="transfer-submit-button"
                type="submit"
                disabled={transferBusy}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white
                  shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold"
              >
                {transferBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Transfer"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
